import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  X,
  Zap,
  HelpCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const plans = [
    {
      name: "Starter",
      description: "Parfait pour commencer",
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: {
        included: [
          "1 Établissement",
          "2 Secrétaires maximum",
          "100 transactions/mois",
          "Rapports mensuels basiques",
          "Support par email",
          "Stockage 1GB"
        ],
        excluded: [
          "Paiements en ligne",
          "Rapports avancés",
          "API d'intégration",
          "Support prioritaire",
          "Formation personnalisée"
        ]
      },
      cta: "Commencer gratuitement",
      popular: false,
      color: "gray"
    },
    {
      name: "Pro",
      description: "Pour les PME en croissance",
      monthlyPrice: 2.99,
      yearlyPrice: 29.99,
      features: {
        included: [
          "5 Établissements",
          "10 Secrétaires maximum",
          "Transactions illimitées",
          "Rapports hebdomadaires détaillés",
          "Paiements en ligne intégrés",
          "Support prioritaire",
          "Stockage 10GB",
          "Export des données"
        ],
        excluded: [
          "API personnalisée",
          "Formation sur site",
          "Conseiller dédié"
        ]
      },
      cta: "Essayer gratuitement 14 jours",
      popular: true,
      color: "blue"
    },
    {
      name: "Enterprise",
      description: "Pour les grandes organisations",
      monthlyPrice: 9.99,
      yearlyPrice: 99.99,
      features: {
        included: [
          "Établissements illimités",
          "Secrétaires illimités",
          "Transactions illimitées",
          "Rapports en temps réel",
          "Paiements en ligne avancés",
          "API personnalisée",
          "Support 24/7",
          "Stockage illimité",
          "Formation personnalisée",
          "Conseiller dédié",
          "Audits de sécurité",
          "SLA 99.9%"
        ],
        excluded: []
      },
      cta: "Contactez-nous",
      popular: false,
      color: "purple"
    }
  ];

  const featuresComparison = [
    {
      name: "Établissements",
      starter: "1",
      pro: "5",
      enterprise: "Illimité"
    },
    {
      name: "Secrétaires",
      starter: "2",
      pro: "10",
      enterprise: "Illimité"
    },
    {
      name: "Transactions/mois",
      starter: "100",
      pro: "Illimité",
      enterprise: "Illimité"
    },
    {
      name: "Paiements en ligne",
      starter: false,
      pro: true,
      enterprise: true
    },
    {
      name: "Rapports avancés",
      starter: false,
      pro: true,
      enterprise: true
    },
    {
      name: "Support 24/7",
      starter: false,
      pro: false,
      enterprise: true
    },
    {
      name: "API personnalisée",
      starter: false,
      pro: false,
      enterprise: true
    },
    {
      name: "SLA garantie",
      starter: false,
      pro: false,
      enterprise: true
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div 
          {...fadeInUp}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Des tarifs simples et transparents
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choisissez le plan qui correspond à vos besoins. Changez de plan à tout moment.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div 
          {...fadeInUp}
          className="flex items-center justify-center mb-12"
        >
          <div className="bg-white rounded-full p-1 inline-flex border border-gray-200">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Facturation mensuelle
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingPeriod === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Facturation annuelle
              <span className="ml-2 text-sm bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                Économisez 20%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ y: -5 }}
              className="relative" // Ajout de relative ici pour positionner le badge
            >
              {/* Badge "Plus populaire" - Maintenant à l'extérieur de la carte */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Plus populaire
                  </div>
                </div>
              )}
              
              {/* Container de la carte */}
              <div className={`relative rounded-2xl overflow-hidden h-full ${
                plan.popular ? 'ring-2 ring-blue-600 shadow-2xl' : 'shadow-lg'
              }`}>
                <div className={`p-8 ${
                  plan.popular ? 'bg-white' : 'bg-white'
                } h-full flex flex-col`}>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600">{plan.description}</p>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-gray-900">
                        ${billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                      </span>
                      <span className="text-gray-600 ml-2">
                        /{billingPeriod === 'monthly' ? 'mois' : 'an'}
                      </span>
                    </div>
                    {plan.monthlyPrice > 0 && billingPeriod === 'yearly' && (
                      <p className="text-green-600 text-sm mt-1">
                        Économisez ${(plan.monthlyPrice * 12 - plan.yearlyPrice).toFixed(2)} par an
                      </p>
                    )}
                  </div>
                  
                  <Link
                    to={plan.name === "Enterprise" ? "/contact" : "/register"}
                    className={`block w-full text-center py-3 rounded-lg font-semibold mb-8 transition-all ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : plan.name === "Starter"
                        ? 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                        : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:opacity-90'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                  
                  {/* Features */}
                  <div className="space-y-4 flex-grow">
                    <h4 className="font-semibold text-gray-900">Inclus :</h4>
                    {plan.features.included.map((feature, i) => (
                      <div key={i} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                    
                    {plan.features.excluded.length > 0 && (
                      <>
                        <h4 className="font-semibold text-gray-900 mt-6">Non inclus :</h4>
                        {plan.features.excluded.map((feature, i) => (
                          <div key={i} className="flex items-center">
                            <X className="h-5 w-5 text-gray-300 mr-3 flex-shrink-0" />
                            <span className="text-gray-400">{feature}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                
                {plan.popular && (
                  <div className="bg-blue-50 p-4 border-t border-blue-100">
                    <div className="flex items-center text-blue-700">
                      <Zap className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">
                        Recommandé pour les PME et startups
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features Comparison Table */}
        <motion.div 
          {...fadeInUp}
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
        >
          <div className="p-8 border-b border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Comparaison détaillée des fonctionnalités
            </h3>
            <p className="text-gray-600">
              Comparez toutes les fonctionnalités pour prendre la meilleure décision
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-6 font-semibold text-gray-900">Fonctionnalité</th>
                  <th className="text-center p-6 font-semibold text-gray-900">Starter</th>
                  <th className="text-center p-6 font-semibold text-gray-900">Pro</th>
                  <th className="text-center p-6 font-semibold text-gray-900">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {featuresComparison.map((feature, index) => (
                  <tr 
                    key={index}
                    className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                  >
                    <td className="p-6 font-medium text-gray-900">{feature.name}</td>
                    <td className="text-center p-6">
                      {typeof feature.starter === 'boolean' ? (
                        feature.starter ? (
                          <Check className="h-6 w-6 text-green-500 mx-auto" />
                        ) : (
                          <X className="h-6 w-6 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span className="font-medium">{feature.starter}</span>
                      )}
                    </td>
                    <td className="text-center p-6">
                      {typeof feature.pro === 'boolean' ? (
                        feature.pro ? (
                          <Check className="h-6 w-6 text-green-500 mx-auto" />
                        ) : (
                          <X className="h-6 w-6 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span className="font-medium">{feature.pro}</span>
                      )}
                    </td>
                    <td className="text-center p-6">
                      {typeof feature.enterprise === 'boolean' ? (
                        feature.enterprise ? (
                          <Check className="h-6 w-6 text-green-500 mx-auto" />
                        ) : (
                          <X className="h-6 w-6 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span className="font-medium">{feature.enterprise}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="p-8 bg-gradient-to-r from-blue-50 to-purple-50 border-t border-gray-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Questions sur les tarifs ?
                </h4>
                <p className="text-gray-600">
                  Notre équipe est là pour vous aider à choisir le bon plan.
                </p>
              </div>
              <div className="flex gap-4">
                <Link
                  to="/contact"
                  className="px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  <HelpCircle className="inline-block h-5 w-5 mr-2" />
                  Poser une question
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Commencer gratuitement
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div 
          {...fadeInUp}
          className="mt-16"
        >
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Questions fréquentes
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "Puis-je changer de plan à tout moment ?",
                a: "Oui, vous pouvez mettre à niveau ou rétrograder votre plan à tout moment depuis votre tableau de bord."
              },
              {
                q: "Y a-t-il des frais de transaction ?",
                a: "Les frais de transaction varient selon votre plan. Consultez notre page tarifaire pour plus de détails."
              },
              {
                q: "Puis-je essayer gratuitement ?",
                a: "Oui, le plan Starter est gratuit à vie. Les plans Pro et Enterprise incluent un essai gratuit de 14 jours."
              },
              {
                q: "Quelle est votre politique de remboursement ?",
                a: "Nous offrons un remboursement sous 30 jours si vous n'êtes pas satisfait du service."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow">
                <h4 className="font-semibold text-gray-900 mb-2">{faq.q}</h4>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;